// Game configuration - All tunable constants in one place
// Adjust these values during playtesting to tune the game feel

export const CONFIG = {
  // ===================
  // PHYSICS
  // ===================
  physics: {
    // Gravity disabled for better control (Asteroids-style flight)
    baseGravity: 0,

    // Zone gravity multipliers (reserved for future optional gravity zones)
    gravityMultipliers: {
      zone1: 0,     // No gravity
      zone2: 0,     // No gravity
      zone3: 0,     // No gravity
      zone4: 0,     // No gravity
    },

    // Drag coefficient - slightly higher for better control
    drag: 0.005,
  },

  // ===================
  // PLAYER SHIP
  // ===================
  player: {
    // Thrust acceleration (units/frame² at 60fps)
    thrustPower: 0.25,

    // Reverse thrust for braking
    reverseThrustMultiplier: 0.7,

    // Rotation speed (degrees per frame at 60fps) - responsive turning
    rotationSpeed: 5,

    // Speed threshold for HUD warning
    speedWarningThreshold: 300,

    // Starting resources
    startingFuel: 100,
    startingShield: 100,
    startingLives: 3,
    startingBombs: 2,

    // Fuel consumption rates (per second) - lower without gravity
    fuelConsumptionThrust: 4,
    fuelConsumptionBoost: 10, // if boost is added later

    // Shield regeneration (per second)
    shieldRegenRate: 0.33, // 1% per 3 seconds per GDD

    // Collision radius for terrain
    collisionRadius: 12,
  },

  // ===================
  // WEAPONS
  // ===================
  weapons: {
    // Pulse cannon
    pulseFireRate: 0.15, // seconds between shots
    pulseSpeed: 500,
    pulseDamage: 1,
    pulseLifetime: 2, // seconds before despawn

    // Smart bombs
    bombCooldown: 0.5,
  },

  // ===================
  // ENEMIES (placeholder for Phase 2)
  // ===================
  enemies: {
    sentryNode: {
      hp: 2,
      points: 100,
      fireRate: 2, // seconds between shots
      detectionRange: 400,
      projectileSpeed: 150,
    },
    pulseTower: {
      hp: 4,
      points: 250,
      fireRate: 0.5,
      detectionRange: 500,
      projectileSpeed: 200,
      leadShots: true, // predictive targeting
    },
    seekerDrone: {
      hp: 1,
      points: 50,
      speed: 100,
      acceleration: 0.1,
      contactDamage: 15, // shield damage percentage
    },
  },

  // ===================
  // POWERUPS (placeholder for Phase 2)
  // ===================
  powerups: {
    fuelPod: { fuelAmount: 25, points: 50 },
    fuelTank: { fuelAmount: 50, points: 100 },
    shieldCrystal: { shieldAmount: 15, points: 75 },
    shieldCore: { shieldAmount: 35, points: 150 },
    smartBomb: { bombAmount: 1, points: 200 },
    pointStar: { points: 500 },
    extraLife: { lives: 1, points: 0 },
    weaponBoost: { duration: 30, points: 100 },
  },

  // ===================
  // DISPLAY
  // ===================
  display: {
    // Canvas dimensions (4:3 aspect ratio)
    width: 800,
    height: 600,

    // Vector glow intensity
    glowIntensity: 8,

    // Phosphor trail fade (0-1, lower = longer trails)
    trailFade: 0.3,
  },

  // ===================
  // COLORS (classic vector palette)
  // ===================
  colors: {
    player: '#00FFFF',      // Cyan
    playerShield: '#00AAFF', // Light blue
    terrain: {
      zone1: '#00FF00',     // Green
      zone2: '#0088FF',     // Blue
      zone3: '#FF4400',     // Red/Orange
      zone4: '#FFFFFF',     // White
    },
    enemies: '#FF6600',      // Orange
    bosses: '#FF0000',       // Red
    hudText: '#FFFF00',      // Yellow
    projectiles: '#FFFFFF',  // White
    fuel: '#00FF00',         // Green
    shield: '#00FFFF',       // Cyan
  },

  // ===================
  // HUD
  // ===================
  hud: {
    // Warning thresholds (percentage)
    fuelWarning: 25,
    fuelCritical: 10,
    shieldWarning: 25,
    shieldCritical: 10,
  },

  // ===================
  // SCORING
  // ===================
  scoring: {
    // Kill streak multipliers
    streakThresholds: [5, 10, 20, 50],
    streakMultipliers: [2, 3, 4, 5],

    // Bonus points
    levelComplete: 1000,
    parTimeBonus: 1500,
    noDamageBonus: 2500,
    secondaryObjective: 2000,
    zoneComplete: 10000,
    bossDefeated: 5000,
  },
} as const;

// Type export for TypeScript
export type GameConfig = typeof CONFIG;
